import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Avatar, InputAdornment, Select, MenuItem, FormControl,
  InputLabel, Skeleton, IconButton, Dialog, DialogTitle, DialogContent,
  LinearProgress, Grid, Divider, useMediaQuery, useTheme, TableSortLabel,
  Paper, Pagination, Stack,
} from '@mui/material';
import {
  Search as SearchIcon, Visibility as ViewIcon,
  QrCode as QrIcon, Close as CloseIcon,
  CheckCircle as CheckIcon, Schedule as PendingIcon,
  PlayArrow as InProgressIcon, PictureAsPdf as PdfIcon,
  Send as SendIcon, LocalHospital as RecommendationIcon,
  Fingerprint as FingerprintIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { patientAPI, locationAPI, reportAPI, isSuperAdmin, maskName, maskMobile, maskEmail } from '../services/api';
import { generateReport } from '../utils/generateReport';

const statusConfig = {
  registered: { color: 'default', icon: <PendingIcon fontSize="small" />, label: 'Registered' },
  in_progress: { color: 'primary', icon: <InProgressIcon fontSize="small" />, label: 'In Progress' },
  completed: { color: 'success', icon: <CheckIcon fontSize="small" />, label: 'Completed' },
};

function PatientList() {
  const routerLocation = useLocation();
  const qp = new URLSearchParams(routerLocation.search);
  const initialStatus      = qp.get('status') || '';
  const initialStartDate   = qp.get('startDate') || '';    // filter Patient.createdAt >= this date
  const initialSessionDate = qp.get('sessionDate') || '';  // filter by TestSession.completedAt date

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [biometricFilter, setBiometricFilter] = useState('');
  const [locations, setLocations] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientDetail, setPatientDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifyResult, setNotifyResult] = useState(null);
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [familyMembers, setFamilyMembers] = useState([]);
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    locationAPI.getAll().then(res => setLocations(res.data.data || [])).catch(() => {});
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchPatients(); }, [page, locationFilter, statusFilter]);

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 15 };
      if (search) params.search = search;
      if (locationFilter) params.locationId = locationFilter;
      if (statusFilter) params.status = statusFilter;
      if (initialStartDate) params.startDate = initialStartDate;
      if (initialSessionDate) params.sessionDate = initialSessionDate;
      const res = await patientAPI.getAll(params);
      setPatients(res.data.data || []);
      setPagination(res.data.pagination || { total: 0, pages: 1 });
    } catch (error) {
      console.error('Error fetching patients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => { setPage(1); fetchPatients(); };

  const handleNotifyPatient = async (patientId) => {
    setNotifyLoading(true);
    setNotifyResult(null);
    try {
      const res = await reportAPI.notifyPatient(patientId);
      setNotifyResult({ success: true, message: 'Report sent via SMS & Email' });
    } catch (error) {
      setNotifyResult({ success: false, message: error.response?.data?.message || 'Failed to send notification' });
    } finally {
      setNotifyLoading(false);
    }
  };

  const handleGenerateReport = async (patientId) => {
    setReportLoading(true);
    try {
      const res = await reportAPI.getByPatientId(patientId);
      generateReport({ ...res.data.data, maskPii: !isSuperAdmin() });
    } catch (error) {
      console.error('Report generation failed:', error);
      alert('Failed to load report data. Please try again.');
    } finally {
      setReportLoading(false);
    }
  };

  const viewPatient = async (patient) => {
    // Reset all detail state first so the dialog shows fresh loading state
    // even when called from within an already-open dialog (family member navigation)
    setSelectedPatient(patient);
    setPatientDetail(null);
    setFamilyMembers([]);
    setNotifyResult(null);
    setDetailLoading(true);
    try {
      const res = await patientAPI.getById(patient._id);
      setPatientDetail(res.data.data);
      try {
        const famRes = await patientAPI.getFamily(patient._id);
        setFamilyMembers(famRes.data?.data || []);
      } catch { setFamilyMembers([]); }
    } catch (error) {
      console.error(error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedPatients = [...patients].sort((a, b) => {
    let aVal, bVal;
    switch (sortField) {
      case 'name':      aVal = (a.name || '').toLowerCase(); bVal = (b.name || '').toLowerCase(); break;
      case 'qrCode':    aVal = (a.qrCode || '').toLowerCase(); bVal = (b.qrCode || '').toLowerCase(); break;
      case 'mobile':    aVal = (a.mobile || ''); bVal = (b.mobile || ''); break;
      case 'gender':    aVal = (a.gender || '').toLowerCase(); bVal = (b.gender || '').toLowerCase(); break;
      case 'location':  aVal = (a.locationId?.name || '').toLowerCase(); bVal = (b.locationId?.name || '').toLowerCase(); break;
      case 'status':    aVal = (a.status || ''); bVal = (b.status || ''); break;
      case 'createdAt': aVal = new Date(a.createdAt); bVal = new Date(b.createdAt); break;
      default:          return 0;
    }
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  // Mobile card view for patient
  const PatientCard = ({ p }) => {
    const sc = statusConfig[p.status] || statusConfig.registered;
    return (
      <Card
        onClick={() => viewPatient(p)}
        sx={{
          mb: 1.5, cursor: 'pointer', transition: 'all 0.2s ease',
          '&:hover': { boxShadow: '0 4px 16px rgba(8,145,178,0.1)' },
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
              <Avatar sx={{
                width: 40, height: 40, fontSize: '0.9rem',
                background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
              }}>
                {p.name?.[0]?.toUpperCase() || '?'}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body1" fontWeight={600} noWrap>{isSuperAdmin() ? p.name : maskName(p.name)}</Typography>
                <Typography variant="caption" color="text.secondary">{isSuperAdmin() ? p.mobile : maskMobile(p.mobile)}</Typography>
              </Box>
            </Box>
            <Chip icon={sc.icon} label={sc.label} size="small" color={sc.color} variant="filled" />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            <Chip icon={<QrIcon />} label={p.qrCode} size="small" variant="outlined"
              sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} />
            {p.locationId?.name && (
              <Chip label={`Last: ${p.locationId.name}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
            )}
            <Chip
              icon={<FingerprintIcon fontSize="small" />}
              label={p.biometricEnrolled ? 'Fingerprint' : 'No Fingerprint'}
              size="small"
              color={p.biometricEnrolled ? 'success' : 'default'}
              variant={p.biometricEnrolled ? 'filled' : 'outlined'}
              sx={p.biometricEnrolled ? {} : { color: '#94a3b8', fontSize: '0.7rem' }}
            />
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        mb: 3, flexWrap: 'wrap', gap: 1,
      }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant={isSmall ? 'h5' : 'h4'} sx={{ fontWeight: 700, color: '#0f172a' }}>
              Patients
            </Typography>
            {!isSuperAdmin() && <Chip label="PII Protected" size="small" sx={{ ml: 1, fontSize: '10px', bgcolor: '#fff3cd', color: '#856404' }} />}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {pagination.total} total patients registered
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{
          display: 'flex', gap: { xs: 1.5, sm: 2 },
          flexWrap: 'wrap', alignItems: 'center',
          p: { xs: 2, sm: 3 },
          '&:last-child': { pb: { xs: 2, sm: 3 } },
        }}>
          <TextField
            size="small" placeholder="Search by name, mobile, or QR..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ flex: 1, minWidth: { xs: '100%', sm: 200 } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#0891b2' }} /></InputAdornment>,
            }}
          />
          <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 140 } }}>
            <InputLabel>Location</InputLabel>
            <Select value={locationFilter} label="Location" onChange={(e) => { setLocationFilter(e.target.value); setPage(1); }}>
              <MenuItem value="">All</MenuItem>
              {locations.map(l => <MenuItem key={l._id} value={l._id}>{l.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 130 } }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="registered">Registered</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 150 } }}>
            <InputLabel>Fingerprint</InputLabel>
            <Select value={biometricFilter} label="Fingerprint" onChange={(e) => { setBiometricFilter(e.target.value); setPage(1); }}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="enrolled">Enrolled</MenuItem>
              <MenuItem value="not_linked">Not Enrolled</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained" onClick={handleSearch} startIcon={<SearchIcon />}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Search
          </Button>
        </CardContent>
      </Card>

      {/* Table (desktop) / Cards (mobile) */}
      {isSmall ? (
        // Mobile card view
        <Box>
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <Card key={i} sx={{ mb: 1.5 }}>
                <CardContent><Skeleton height={60} /></CardContent>
              </Card>
            ))
          ) : patients.length === 0 ? (
            <Card sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary">No patients found</Typography>
            </Card>
          ) : (
            patients.map((p) => <PatientCard key={p._id} p={p} />)
          )}
        </Box>
      ) : (
        // Desktop table view
        <Card>
          <Box sx={{ overflowX: 'auto' }}>
            <TableContainer sx={{ minWidth: 700 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    {[
                      { label: 'Patient', field: 'name' },
                      { label: 'QR Code', field: 'qrCode' },
                      { label: 'Mobile', field: 'mobile' },
                      { label: 'Gender', field: 'gender' },
                      { label: 'Location', field: 'location' },
                      { label: 'Status', field: 'status' },
                      { label: 'Fingerprint', field: 'biometric' },
                      { label: 'Registered', field: 'createdAt' },
                      { label: 'Last Visit', field: 'lastVisitAt' },
                    ].map(({ label, field }) => (
                      <TableCell key={field} sortDirection={sortField === field ? sortOrder : false}
                        sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                        <TableSortLabel
                          active={sortField === field}
                          direction={sortField === field ? sortOrder : 'asc'}
                          onClick={() => handleSort(field)}
                        >
                          {label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ fontWeight: 700 }}>View</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 11 }).map((_, j) => (
                          <TableCell key={j}><Skeleton /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : patients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} sx={{ textAlign: 'center', py: 6 }}>
                        <Typography color="text.secondary">No patients found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedPatients.map((p) => {
                      const sc = statusConfig[p.status] || statusConfig.registered;
                      return (
                        <TableRow key={p._id} hover sx={{ cursor: 'pointer' }} onClick={() => viewPatient(p)}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar sx={{
                                width: 36, height: 36, fontSize: '0.85rem',
                                background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
                              }}>
                                {p.name?.[0]?.toUpperCase() || '?'}
                              </Avatar>
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                  <Typography variant="body2" fontWeight={600}>{isSuperAdmin() ? p.name : maskName(p.name)}</Typography>
                                  {p.memberNumber > 1 && (
                                    <Chip
                                      label={`👨‍👩‍👧 ${p.relation ? p.relation.charAt(0).toUpperCase() + p.relation.slice(1) : 'Family'}`}
                                      size="small"
                                      sx={{ fontSize: '0.65rem', height: 18, bgcolor: '#ede9fe', color: '#6d28d9', fontWeight: 600 }}
                                    />
                                  )}
                                </Box>
                                {p.dateOfBirth && (
                                  <Typography variant="caption" color="text.secondary">
                                    Age: {p.age || Math.floor((Date.now() - new Date(p.dateOfBirth)) / 31557600000)}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip icon={<QrIcon />} label={p.qrCode} size="small" variant="outlined"
                              sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{isSuperAdmin() ? p.mobile : maskMobile(p.mobile)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{p.gender || '-'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{p.locationId?.name || '-'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip icon={sc.icon} label={sc.label} size="small" color={sc.color} variant="filled" />
                          </TableCell>
                          <TableCell>
                            {p.biometricEnrolled ? (
                              <Chip icon={<FingerprintIcon fontSize="small" />} label="Enrolled" size="small" color="success" variant="filled" />
                            ) : (
                              <Chip label="Not Enrolled" size="small" variant="outlined" sx={{ color: '#94a3b8', fontSize: '0.72rem' }} />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(p.createdAt).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {p.lastVisitAt ? new Date(p.lastVisitAt).toLocaleDateString() : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); viewPatient(p); }}>
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, px: 2, py: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {((page - 1) * 15) + 1}–{Math.min(page * 15, pagination.total)} of {pagination.total} patients
              </Typography>
              <Stack>
                <Pagination
                  count={pagination.pages}
                  page={page}
                  onChange={(_, val) => setPage(val)}
                  color="primary"
                  size={isSmall ? 'small' : 'medium'}
                  siblingCount={isSmall ? 0 : 1}
                  showFirstButton
                  showLastButton
                />
              </Stack>
            </Box>
          )}
        </Card>
      )}

      {/* Patient Detail Dialog */}
      <Dialog
        open={Boolean(selectedPatient)}
        onClose={() => { setSelectedPatient(null); setPatientDetail(null); setNotifyResult(null); setFamilyMembers([]); }}
        maxWidth="md" fullWidth
        fullScreen={isSmall}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={700}>Patient Details</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {patientDetail && (
              <>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={reportLoading ? null : <PdfIcon />}
                  disabled={reportLoading}
                  onClick={() => handleGenerateReport(selectedPatient._id)}
                  sx={{
                    background: 'linear-gradient(135deg, #00A8E8, #005B8F)',
                    fontWeight: 700, borderRadius: 2, fontSize: '0.75rem',
                    '&:hover': { background: 'linear-gradient(135deg, #005B8F, #00A8E8)' },
                  }}
                >
                  {reportLoading ? 'Loading...' : 'Generate Report'}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={notifyLoading ? null : <SendIcon />}
                  disabled={notifyLoading}
                  onClick={() => handleNotifyPatient(selectedPatient._id)}
                  sx={{
                    background: notifyResult?.success
                      ? 'linear-gradient(135deg, #16a34a, #15803d)'
                      : 'linear-gradient(135deg, #86E000, #4caf50)',
                    color: '#fff',
                    fontWeight: 700, borderRadius: 2, fontSize: '0.75rem',
                    '&:hover': { background: 'linear-gradient(135deg, #4caf50, #86E000)' },
                  }}
                >
                  {notifyLoading ? 'Sending...' : notifyResult?.success ? 'Sent ✓' : 'Send Report'}
                </Button>
                {notifyResult && !notifyResult.success && (
                  <Typography variant="caption" color="error">{notifyResult.message}</Typography>
                )}
              </>
            )}
            <IconButton onClick={() => { setSelectedPatient(null); setPatientDetail(null); setNotifyResult(null); setFamilyMembers([]); }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ py: 4 }}>
              <Skeleton height={40} /><Skeleton height={40} /><Skeleton height={200} />
            </Box>
          ) : patientDetail ? (
            <Box>
              {/* Patient Info */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Name</Typography>
                  <Typography variant="body1" fontWeight={600}>{isSuperAdmin() ? patientDetail.patient?.name : maskName(patientDetail.patient?.name)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">QR Code</Typography>
                  <Chip icon={<QrIcon />} label={patientDetail.patient?.qrCode} variant="outlined" sx={{ fontFamily: 'monospace' }} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Mobile</Typography>
                  <Typography variant="body1">{isSuperAdmin() ? patientDetail.patient?.mobile : maskMobile(patientDetail.patient?.mobile)}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Gender</Typography>
                  <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>{patientDetail.patient?.gender || '-'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip
                    label={patientDetail.patient?.status || 'registered'}
                    size="small"
                    color={statusConfig[patientDetail.patient?.status]?.color || 'default'}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Visited Locations</Typography>
                  {(() => {
                    const seen = new Set();
                    const locs = (patientDetail.sessions || [])
                      .filter(s => s.locationId?.name && !seen.has(s.locationId.name) && seen.add(s.locationId.name))
                      .map(s => s.locationId.name);
                    return locs.length > 0
                      ? <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {locs.map(l => <Chip key={l} label={l} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />)}
                        </Box>
                      : <Typography variant="body1">{patientDetail.patient?.locationId?.name || '-'}</Typography>;
                  })()}
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Fingerprint / Biometric Info */}
              <Card variant="outlined" sx={{ mb: 2, borderColor: '#e0f2fe', bgcolor: '#f0f9ff' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <FingerprintIcon sx={{ color: '#0891b2', fontSize: 20 }} />
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#0891b2' }}>
                      Biometric
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">Fingerprint Enrolled</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {patientDetail.patient?.biometricEnrolled ? '✅ Yes' : '❌ No'}
                      </Typography>
                    </Grid>
                    {(patientDetail.patient?.fingerprintEnrolledAt || patientDetail.patient?.fingerprintEnrollmentDate) && (
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">Enrolled On</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {new Date(patientDetail.patient.fingerprintEnrolledAt || patientDetail.patient.fingerprintEnrollmentDate).toLocaleDateString()}
                        </Typography>
                      </Grid>
                    )}
                    {patientDetail.patient?.lastBiometricAuth && (
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">Last Login</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {new Date(patientDetail.patient.lastBiometricAuth).toLocaleString()}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              <Divider sx={{ my: 2 }} />

              {/* Sessions */}
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, color: '#0f172a' }}>
                Test Sessions
              </Typography>
              {(patientDetail.sessions || []).length === 0 ? (
                <Typography color="text.secondary">No test sessions yet</Typography>
              ) : (
                <>
                  {patientDetail.sessions.map((session) => {
                    const completed = session.stationResults?.filter(r => r.status === 'completed').length || 0;
                    const total = session.totalStations || 1;
                    const progress = Math.round((completed / total) * 100);
                    return (
                      <Card key={session._id} variant="outlined" sx={{ mb: 2, p: 2, borderColor: '#e0f2fe' }}>
                        <Box sx={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          mb: 1.5, flexWrap: 'wrap', gap: 1,
                        }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            Session - {new Date(session.createdAt).toLocaleString()}
                          </Typography>
                          <Chip
                            label={session.status}
                            size="small"
                            color={statusConfig[session.status]?.color || 'default'}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <LinearProgress
                            variant="determinate" value={progress}
                            sx={{
                              flex: 1, height: 8, borderRadius: 4, bgcolor: '#e0f2fe',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 4,
                                background: 'linear-gradient(90deg, #0891b2, #06b6d4)',
                              },
                            }}
                          />
                          <Typography variant="body2" fontWeight={600} sx={{ color: '#0891b2' }}>{progress}%</Typography>
                        </Box>
                        {/* Station completion status only */}
                        <Grid container spacing={1}>
                          {(session.stationResults || []).map((sr) => (
                            <Grid item xs={6} sm={4} md={3} key={sr._id}>
                              <Box sx={{
                                p: 1, borderRadius: 2, bgcolor: sr.status === 'completed' ? '#ecfdf5' : '#f8fafc',
                                border: '1px solid', borderColor: sr.status === 'completed' ? '#a7f3d0' : '#e2e8f0',
                                display: 'flex', alignItems: 'center', gap: 0.5,
                              }}>
                                {sr.status === 'completed' ? (
                                  <CheckIcon sx={{ fontSize: 14, color: '#10b981' }} />
                                ) : (
                                  <PendingIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                                )}
                                <Typography variant="caption" fontWeight={600}>
                                  {sr.stationId?.name || `Station ${sr.stationId?.stationNumber || sr.stationNumber || '?'}`}
                                </Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>

                        {/* Doctor Recommendation */}
                        {session.recommendation?.text ? (
                          <Box sx={{
                            mt: 2, p: 1.5, borderRadius: 2,
                            bgcolor: '#f0fdf4', border: '1px solid #bbf7d0',
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                              <RecommendationIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                              <Typography variant="caption" fontWeight={700} sx={{ color: '#15803d', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Doctor's Recommendation
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: '#166534', mb: 0.5 }}>
                              {session.recommendation.text}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#4ade80', fontStyle: 'italic' }}>
                              — {session.recommendation.doctorName}
                              {session.recommendation.doctorDesignation ? `, ${session.recommendation.doctorDesignation}` : ''}
                              {session.recommendation.addedAt ? ` · ${new Date(session.recommendation.addedAt).toLocaleDateString()}` : ''}
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{
                            mt: 2, px: 1.5, py: 0.75, borderRadius: 2, display: 'inline-flex',
                            alignItems: 'center', gap: 0.75, bgcolor: '#f8fafc',
                            border: '1px dashed #cbd5e1',
                          }}>
                            <RecommendationIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                              Doctor's Recommendation: <strong>NA</strong>
                            </Typography>
                          </Box>
                        )}
                      </Card>
                    );
                  })}

                  {/* Combined Test Results Summary */}
                  {(() => {
                    // Merge all station testData from the latest session
                    const latestSession = patientDetail.sessions[0];
                    if (!latestSession) return null;
                    let d = {};
                    (latestSession.stationResults || []).forEach(sr => {
                      if (sr.testData) d = { ...d, ...sr.testData };
                    });
                    if (Object.keys(d).length === 0) return null;

                    const fv = (field) => field?.value ?? field ?? null;
                    const fv2 = (field) => { const v = fv(field); return v != null && !isNaN(v) ? parseFloat(parseFloat(v).toFixed(2)) : v; };
                    const fu = (field) => field?.unit || '';
                    const row = (label, value, unit) => value != null ? (
                      <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #f1f5f9' }}>
                        <Typography variant="body2" color="text.secondary">{label}</Typography>
                        <Typography variant="body2" fontWeight={600}>{value} {unit}</Typography>
                      </Box>
                    ) : null;

                    const sections = [
                      {
                        title: 'Basic Vitals',
                        rows: [
                          row('Body Temperature', fv(d.bodyTemperature), fu(d.bodyTemperature) || '°F'),
                          row('Oxygen Saturation (SpO2)', fv(d.oxygenSaturation), fu(d.oxygenSaturation) || '%'),
                          row('Respiratory Rate', fv(d.respiratoryRate), fu(d.respiratoryRate) || 'breaths/min'),
                          row('Heart Rate', fv(d.heartRate), fu(d.heartRate) || 'bpm'),
                          row('Systolic BP', d.bloodPressure?.systolic, 'mmHg'),
                          row('Diastolic BP', d.bloodPressure?.diastolic, 'mmHg'),
                        ].filter(Boolean),
                      },
                      {
                        title: 'Body Composition',
                        rows: [
                          row('Height', fv(d.height), fu(d.height) || 'cm'),
                          row('Weight', fv(d.weight), fu(d.weight) || 'kg'),
                          row('BMI', fv2(d.bmi), fu(d.bmi) || 'kg/m²'),
                          row('Waist Circumference', fv(d.waistCircumference), fu(d.waistCircumference) || 'cm'),
                          row('Hip Circumference', fv(d.hipCircumference), fu(d.hipCircumference) || 'cm'),
                          row('Body Fat %', fv(d.bodyFatPercentage), fu(d.bodyFatPercentage) || '%'),
                          row('Muscle Mass', fv(d.muscleMass), fu(d.muscleMass) || 'kg'),
                          row('Bone Mass', fv(d.boneMass), fu(d.boneMass) || 'kg'),
                          row('Body Water %', fv(d.bodyWaterPercentage), fu(d.bodyWaterPercentage) || '%'),
                          row('Visceral Fat', fv(d.visceralFat), ''),
                          row('BMR', fv(d.bmr), fu(d.bmr) || 'kcal/day'),
                        ].filter(Boolean),
                      },
                      {
                        title: 'Blood Tests',
                        rows: [
                          row('Hemoglobin', fv(d.hemoglobin), fu(d.hemoglobin) || 'g/dL'),
                          row('Fasting Glucose', fv(d.bloodGlucoseFasting) ?? fv(d.bloodGlucose?.fasting), fu(d.bloodGlucoseFasting) || 'mg/dL'),
                          row('Random Glucose', fv(d.bloodGlucoseRandom) ?? fv(d.bloodGlucose?.random), fu(d.bloodGlucoseRandom) || 'mg/dL'),
                          row('HbA1c', fv(d.hba1c), fu(d.hba1c) || '%'),
                        ].filter(Boolean),
                      },
                      {
                        title: 'Spirometry (Lung Function)',
                        rows: (() => {
                          const s = d.spirometryTest;
                          if (!s) return [];
                          return [
                            row('FVC', fv(s.fvc), 'L'),
                            row('FEV1', fv(s.fev1), 'L'),
                            row('PEF', fv(s.pef), 'L/s'),
                            row('FEV1/FVC', fv(s.fev1_fvc), '%'),
                            row('FEF25', fv(s.fef25), 'L/s'),
                            row('FEF50', fv(s.fef50), 'L/s'),
                            row('FEF75', fv(s.fef75), 'L/s'),
                            row('FEF25-75', fv(s.fef25_75), 'L/s'),
                          ].filter(Boolean);
                        })(),
                      },
                      {
                        title: 'Infectious Disease Tests',
                        rows: [
                          row('Malaria Test', d.malariaTest, ''),
                          row('Pregnancy Test', d.pregnancyTest, ''),
                          row('COVID-19 Test', d.covidTest, ''),
                        ].filter(Boolean),
                      },
                    ].filter(s => s.rows.length > 0);

                    if (sections.length === 0) return null;

                    return (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, color: '#0f172a' }}>
                          Test Results Summary
                        </Typography>
                        <Grid container spacing={2}>
                          {sections.map(section => (
                            <Grid item xs={12} sm={6} key={section.title}>
                              <Card variant="outlined" sx={{ p: 2, borderColor: '#e0f2fe', height: '100%' }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#0891b2' }}>
                                  {section.title}
                                </Typography>
                                {section.rows}
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </>
                    );
                  })()}
                </>
              )}

              {/* Family Group */}
              {familyMembers.length > 0 && (() => {
                const relationMeta = {
                  self:    { color: '#6d28d9', bg: '#f5f3ff', label: 'Primary' },
                  spouse:  { color: '#be185d', bg: '#fdf2f8', label: 'Spouse'  },
                  child:   { color: '#1d4ed8', bg: '#eff6ff', label: 'Child'   },
                  parent:  { color: '#065f46', bg: '#ecfdf5', label: 'Parent'  },
                  sibling: { color: '#b45309', bg: '#fffbeb', label: 'Sibling' },
                  other:   { color: '#374151', bg: '#f9fafb', label: 'Other'   },
                };
                const sessionMeta = {
                  completed:   { color: '#16a34a', bg: '#dcfce7', label: 'Completed',   dot: '🟢' },
                  in_progress: { color: '#2563eb', bg: '#dbeafe', label: 'In Progress', dot: '🔵' },
                  pending:     { color: '#d97706', bg: '#fef3c7', label: 'Pending',      dot: '🟡' },
                  no_session:  { color: '#6b7280', bg: '#f3f4f6', label: 'No Session',   dot: '⚪' },
                };
                const currentPatient = patientDetail?.patient;
                const currentMeta = relationMeta['self'];
                const currentSession = sessionMeta[currentPatient?.status] || sessionMeta['no_session'];

                return (
                  <Box sx={{ mt: 3 }}>
                    {/* Header */}
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5, mb: 2,
                      p: 1.5, borderRadius: 2,
                      background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
                      border: '1px solid #c4b5fd',
                    }}>
                      <Typography sx={{ fontSize: 22 }}>👨‍👩‍👧</Typography>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#5b21b6', lineHeight: 1.2 }}>
                          Family Group
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#7c3aed' }}>
                          {familyMembers.length + 1} members linked · same mobile number
                        </Typography>
                      </Box>
                    </Box>

                    {/* Cards row */}
                    <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>

                      {/* Primary patient card (current) */}
                      <Card variant="outlined" sx={{
                        minWidth: 180, maxWidth: 200, flexShrink: 0,
                        border: `2px solid ${currentMeta.color}`,
                        borderRadius: 3, bgcolor: currentMeta.bg,
                        position: 'relative',
                      }}>
                        {/* Primary badge */}
                        <Box sx={{
                          position: 'absolute', top: -1, left: 12,
                          bgcolor: currentMeta.color, color: '#fff',
                          fontSize: '0.6rem', fontWeight: 700,
                          px: 1, py: 0.3, borderRadius: '0 0 6px 6px',
                          letterSpacing: 0.5,
                        }}>
                          ⭐ PRIMARY
                        </Box>
                        <CardContent sx={{ pt: 3, pb: '12px !important', px: 1.5 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
                            <Avatar sx={{
                              width: 48, height: 48, fontSize: '1.1rem', fontWeight: 700,
                              bgcolor: currentMeta.color, color: '#fff',
                            }}>
                              {currentPatient?.name?.[0]?.toUpperCase() || '?'}
                            </Avatar>
                            <Typography variant="body2" fontWeight={700} align="center" sx={{ lineHeight: 1.3 }}>
                              {isSuperAdmin() ? currentPatient?.name : maskName(currentPatient?.name)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" align="center">
                              {currentPatient?.age ? `Age ${currentPatient.age}` : ''}
                              {currentPatient?.age && currentPatient?.gender ? ' · ' : ''}
                              {currentPatient?.gender ? currentPatient.gender.charAt(0).toUpperCase() : ''}
                            </Typography>
                            {/* Biometric */}
                            <Box sx={{
                              display: 'flex', alignItems: 'center', gap: 0.5,
                              bgcolor: currentPatient?.biometricEnrolled ? '#dcfce7' : '#fee2e2',
                              borderRadius: 1, px: 1, py: 0.25,
                            }}>
                              <FingerprintIcon sx={{ fontSize: 12, color: currentPatient?.biometricEnrolled ? '#16a34a' : '#dc2626' }} />
                              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: currentPatient?.biometricEnrolled ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                                {currentPatient?.biometricEnrolled ? 'Enrolled' : 'Not enrolled'}
                              </Typography>
                            </Box>
                            {/* Session status */}
                            <Box sx={{ bgcolor: currentSession.bg, borderRadius: 1, px: 1, py: 0.25 }}>
                              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: currentSession.color, fontWeight: 600 }}>
                                {currentSession.dot} {currentSession.label}
                              </Typography>
                            </Box>
                            <Typography variant="caption" sx={{
                              fontSize: '0.6rem', color: '#6b7280',
                              bgcolor: '#f3f4f6', borderRadius: 1, px: 1, py: 0.25,
                            }}>
                              Viewing now
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>

                      {/* Connector */}
                      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <Box sx={{ width: 2, height: 60, bgcolor: '#c4b5fd', borderRadius: 1 }} />
                        <Box sx={{ width: 16, height: 2, bgcolor: '#c4b5fd' }} />
                      </Box>

                      {/* Family member cards */}
                      {familyMembers.map((m) => {
                        const rel = relationMeta[m.relation] || relationMeta['other'];
                        const sess = sessionMeta[m.sessionStatus] || sessionMeta['no_session'];
                        return (
                          <Card key={m.id} variant="outlined" sx={{
                            minWidth: 175, maxWidth: 195, flexShrink: 0,
                            border: `1.5px solid ${rel.color}40`,
                            borderRadius: 3, bgcolor: rel.bg,
                            transition: 'box-shadow 0.2s, transform 0.2s',
                            cursor: 'pointer',
                            '&:hover': { boxShadow: `0 4px 16px ${rel.color}30`, transform: 'translateY(-2px)' },
                          }}
                            onClick={() => viewPatient({ _id: m.id, name: m.name })}
                          >
                            <CardContent sx={{ pt: 2, pb: '12px !important', px: 1.5 }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
                                <Avatar sx={{
                                  width: 44, height: 44, fontSize: '1rem', fontWeight: 700,
                                  bgcolor: rel.color, color: '#fff', opacity: 0.9,
                                }}>
                                  {m.name?.[0]?.toUpperCase() || '?'}
                                </Avatar>
                                <Typography variant="body2" fontWeight={700} align="center" sx={{ lineHeight: 1.3 }}>
                                  {isSuperAdmin() ? m.name : maskName(m.name)}
                                </Typography>
                                {/* Relation badge */}
                                <Box sx={{
                                  bgcolor: rel.color, color: '#fff',
                                  borderRadius: 1, px: 1, py: 0.2,
                                }}>
                                  <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'capitalize' }}>
                                    {rel.label}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary" align="center">
                                  {m.age ? `Age ${m.age}` : ''}
                                  {m.age && m.gender ? ' · ' : ''}
                                  {m.gender ? m.gender.charAt(0).toUpperCase() : ''}
                                </Typography>
                                {/* Biometric */}
                                <Box sx={{
                                  display: 'flex', alignItems: 'center', gap: 0.5,
                                  bgcolor: m.biometricEnrolled ? '#dcfce7' : '#fee2e2',
                                  borderRadius: 1, px: 1, py: 0.25,
                                }}>
                                  <FingerprintIcon sx={{ fontSize: 12, color: m.biometricEnrolled ? '#16a34a' : '#dc2626' }} />
                                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: m.biometricEnrolled ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                                    {m.biometricEnrolled ? 'Enrolled' : 'Not enrolled'}
                                  </Typography>
                                </Box>
                                {/* Session status */}
                                <Box sx={{ bgcolor: sess.bg, borderRadius: 1, px: 1, py: 0.25 }}>
                                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: sess.color, fontWeight: 600 }}>
                                    {sess.dot} {sess.label}
                                  </Typography>
                                </Box>
                                {/* View details button */}
                                <Button
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    mt: 0.5, fontSize: '0.65rem', py: 0.3, px: 1.5,
                                    borderColor: rel.color, color: rel.color, borderRadius: 2,
                                    '&:hover': { bgcolor: rel.color, color: '#fff' },
                                  }}
                                  onClick={(e) => { e.stopPropagation(); viewPatient({ _id: m.id, name: m.name }); }}
                                >
                                  View Details
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default PatientList;
